import { http } from '@/shared/api/api-client';
import type { PagedResponse } from '@/shared/api/api-types';

import type {
  Region,
  RegionListParams,
  RegionOrderItem,
  RegionPayload,
} from '../types/region.types';

/**
 * Region HTTP calls — all against the Admin API (UT system
 * `AdminApis/Catalogue`), which enforces permissions and scope and audits
 * every change. One named function per endpoint (§12).
 */

interface AdminPage<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

/** The table's sort keys, mapped to the Admin API's whitelist. */
const SORTS: Record<string, string> = {
  sortOrder: 'position',
  name: 'name',
};

/** Paged, searched, filtered and sorted on the server (by storefront position by default). */
export async function getRegions(
  params: RegionListParams,
  signal?: AbortSignal,
): Promise<PagedResponse<Region>> {
  const page = await http.get<AdminPage<Region>>('/admin/regions', {
    params: {
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
      isActive: params.isActive,
      sort: (params.sort && SORTS[params.sort]) || 'position',
      descending: params.direction === 'desc',
    },
    signal,
  });
  return {
    items: page.items,
    page: page.page,
    pageSize: page.pageSize,
    totalItems: page.totalCount,
    totalPages: page.totalPages,
  };
}

export interface RegionOption {
  id: number;
  name: string;
  code: string;
  isActive: boolean;
}

/** Every region, in storefront order, for pickers (inactive ones included and marked). */
export async function getRegionOptions(signal?: AbortSignal): Promise<RegionOption[]> {
  const page = await http.get<AdminPage<Region>>('/admin/regions', {
    params: { page: 1, pageSize: 100 },
    signal,
  });
  return page.items.map((region) => ({
    id: region.id,
    name: region.name,
    code: region.code,
    isActive: region.isActive,
  }));
}

/**
 * Every region in storefront store order — the order of the store picker on a
 * brand's page. Shown exactly as returned, never re-sorted here.
 */
export async function getRegionOrder(signal?: AbortSignal): Promise<RegionOrderItem[]> {
  return http.get<RegionOrderItem[]>('/admin/regions/order', { signal });
}

/**
 * Saves the storefront region order (catalogue.manage, all-country scope): every
 * region exactly once, first to last. The backend numbers them 1..n and audits
 * it; a stale or incomplete list is refused with 409 `REGION_ORDER_INCOMPLETE`.
 */
export async function saveRegionOrder(regionIds: number[], reason: string): Promise<number> {
  const result = await http.put<{ changed: number }>('/admin/regions/order', { regionIds, reason });
  return result.changed;
}

/** Returns the new region's id. New regions are active and go after the last one. */
export async function createRegion(payload: RegionPayload): Promise<number> {
  return http.post<number>('/admin/regions', payload);
}

export async function updateRegion(regionId: number, payload: RegionPayload): Promise<void> {
  await http.put<number>(`/admin/regions/${regionId}`, payload);
}

/**
 * Deletes a region no denomination belongs to. The Admin API refuses one still
 * in use with 409 `REGION_IN_USE` (checked again under a lock at delete time)
 * and the system region with 409 `REGION_IS_SYSTEM`.
 */
export async function deleteRegion(regionId: number, reason: string | null): Promise<void> {
  await http.delete<number>(`/admin/regions/${regionId}`, { data: { reason } });
}

/** Returns how many records changed (0 when already in that state). */
export async function setRegionStatus(
  regionId: number,
  isActive: boolean,
  reason: string | null,
): Promise<number> {
  const result = await http.post<{ changed: number }>(`/admin/regions/${regionId}/status`, {
    isActive,
    reason,
  });
  return result.changed;
}
